import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { apiCall } from "../config/api";
import { useAuth } from "../contexts/AuthContext";

const formatDate = (timestamp) => {
  if (!timestamp) return "Tarih Yok";

  const date =
    timestamp instanceof Date
      ? timestamp
      : typeof timestamp.toDate === "function"
      ? timestamp.toDate()
      : new Date(timestamp);

  return date.toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ComplaintListPage = () => {
  const { currentUser } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchComplaints();
    }
  }, [currentUser]);

  const fetchComplaints = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("📡 Şikayetler yükleniyor...");
      const response = await apiCall("/api/admin/pins/reported");

      if (response.success && response.reports) {
        console.log(`✅ ${response.reports.length} şikayet bulundu`);
        setComplaints(response.reports);
      } else {
        throw new Error(response.error || "Şikayetler yüklenemedi");
      }
    } catch (err) {
      console.error("❌ Şikayet yükleme hatası:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (pinId, action) => {
    setActionLoading(pinId);
    try {
      const response = await apiCall(`/api/admin/pins/${pinId}/action`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });

      if (response.success) {
        // Başarılı işlem - listeyi güncelle
        setComplaints((prev) => prev.filter((c) => c.pinId !== pinId));
        setDetailsOpen(false);
        alert(getActionMessage(action));
      } else {
        throw new Error(response.error || "İşlem başarısız");
      }
    } catch (err) {
      alert(`Hata: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getActionMessage = (action) => {
    const messages = {
      approve: "✅ Pin onaylandı ve şikayetler temizlendi",
      quarantine: "⚠️ Pin karantinaya alındı",
      delete: "🗑️ Pin kalıcı olarak silindi",
    };
    return messages[action] || "İşlem tamamlandı";
  };

  const openDetails = (complaint) => {
    setSelectedComplaint(complaint);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedComplaint(null);
  };

  const getReasonColor = (reason) => {
    const colors = {
      "Uygunsuz içerik": "error",
      Spam: "warning",
      "Yanıltıcı bilgi": "info",
      "Telif hakkı ihlali": "error",
      Diğer: "default",
    };
    return colors[reason] || "default";
  };

  const getReasonIcon = (reason) => {
    const icons = {
      "Uygunsuz içerik": <WarningIcon fontSize="small" />,
      Spam: <InfoIcon fontSize="small" />,
      "Yanıltıcı bilgi": <InfoIcon fontSize="small" />,
      "Telif hakkı ihlali": <WarningIcon fontSize="small" />,
      Diğer: <InfoIcon fontSize="small" />,
    };
    return icons[reason] || <InfoIcon fontSize="small" />;
  };

  const getStatusColor = (isVisible) => {
    return isVisible ? "success" : "error";
  };

  const getStatusText = (isVisible) => {
    return isVisible ? "✅ Aktif" : "🔒 Karantinada";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Hata:</strong> {error}
        </Alert>
        <Button variant="contained" onClick={fetchComplaints}>
          Tekrar Dene
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          📋 Şikayet Listesi
        </Typography>
        <Chip
          label={`${complaints.length} Şikayet`}
          color="primary"
          size="large"
          sx={{ fontSize: "1.1rem", px: 2, py: 3 }}
        />
      </Box>

      {/* Empty State */}
      {complaints.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            🎉 Harika! Hiç şikayet yok
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Şu anda incelemeniz gereken şikayet bulunmuyor.
          </Typography>
        </Paper>
      ) : (
        /* Complaints Grid */
        <Grid container spacing={3}>
          {complaints.map((complaint) => (
            <Grid item xs={12} md={6} lg={4} key={complaint.pinId}>
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Pin Info */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      component="div"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      📍 {complaint.pin?.name || "İsimsiz Pin"}
                    </Typography>

                    {/* Owner Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Avatar
                        src={complaint.pin?.owner?.photoURL}
                        sx={{ width: 32, height: 32 }}
                      >
                        {(complaint.pin?.owner?.displayName || "?")[0]}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        {complaint.pin?.owner?.displayName ||
                          "Bilinmeyen Kullanıcı"}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Chip
                      label={getStatusText(complaint.isPubliclyVisible)}
                      color={getStatusColor(complaint.isPubliclyVisible)}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Complaint Stats */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h4"
                      color="error.main"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      {complaint.reportCount}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        şikayet
                      </Typography>
                    </Typography>

                    {/* Reasons */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      {Array.from(
                        new Set(complaint.reports?.map((r) => r.reason) || [])
                      )
                        .slice(0, 3)
                        .map((reason, idx) => (
                          <Chip
                            key={idx}
                            icon={getReasonIcon(reason)}
                            label={reason}
                            color={getReasonColor(reason)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => openDetails(complaint)}
                  >
                    📋 Detaylar
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            📍 {selectedComplaint?.pin?.name || "İsimsiz Pin"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedComplaint?.reportCount} şikayet •{" "}
            {getStatusText(selectedComplaint?.isPubliclyVisible)}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {/* Pin Owner */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pin Sahibi
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={selectedComplaint?.pin?.owner?.photoURL}
                sx={{ width: 48, height: 48 }}
              >
                {(selectedComplaint?.pin?.owner?.displayName || "?")[0]}
              </Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedComplaint?.pin?.owner?.displayName ||
                    "Bilinmeyen Kullanıcı"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedComplaint?.pin?.owner?.email || "E-posta yok"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Complaints List */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Şikayetler ({selectedComplaint?.reports?.length || 0})
          </Typography>
          <List>
            {selectedComplaint?.reports?.map((report, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar src={report.reporter?.photoURL}>
                      {(report.reporter?.displayName || "?")[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {report.reporter?.displayName ||
                            "Bilinmeyen Kullanıcı"}
                        </Typography>
                        <Chip
                          label={report.reason}
                          color={getReasonColor(report.reason)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(report.reportedAt)}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < (selectedComplaint?.reports?.length || 0) - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeDetails}>İptal</Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleAction(selectedComplaint?.pinId, "approve")}
            disabled={actionLoading === selectedComplaint?.pinId}
          >
            Onayla
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<VisibilityOffIcon />}
            onClick={() => handleAction(selectedComplaint?.pinId, "quarantine")}
            disabled={actionLoading === selectedComplaint?.pinId}
          >
            Karantina
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleAction(selectedComplaint?.pinId, "delete")}
            disabled={actionLoading === selectedComplaint?.pinId}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComplaintListPage;
